import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { SocketContext } from "./SocketContext";
import audioFile from "../assets/music/happy-pop.mp3";
import AppContext from "../AppContext";
import useChat from "../hooks/useChat";
import useUsers from "../hooks/useUsers";
import _ from "lodash";

const ChatSessionContext = React.createContext({
  sessions: undefined,
  setSessions: () => {},
  currentSession: undefined,
  setCurrentSession: () => {},
  getCurrentSession: () => {},
  messages: undefined,
  setMessages: () => {},
  chatMetadata: undefined,
  setChatMetadata: () => {},
  isSupport: undefined,
  setIsSupport: () => {},
  startChat: (recipientId, currentUserId, { work }) => {},
  toggleChatSupportWindow: () => {},
  isNewMessage: undefined,
  setLatestMessage: (sessionId, message, createdOn) => {},
  hideSession: (sessionId) => {},
  /**
   * Return whether the date is older than current hidden period
   * @param {string | Date} dateToCompare
   */
  isHiddenMessage: (dateToCompare) => {},
  pinSession: (sessionId) => {},
  unpinSession: (sessionId) => {},
  pinMessage: (chatId) => {},
  unpinMessage: (chatId) => {},
  clearCurrentSession: () => {},
  isMinimizeHidden: false,
  setIsMinimizeHidden: (hidden) => {},
});

export const MESSAGE_TYPE = {
  LOADING: "LOADING",
  MESSAGE: "MSG",
  FORM: "FORM",
};

export const FORM_TYPE = {
  CREATE_QUOTATION: "CREATE_QUOTATION",
  PAY: "PAY",
  RATE_SUPPORT: "RATE_SUPPORT",
  SUBMIT_DRAFT: "SUBMIT_DRAFT",
};

export const ChatSessionProvider = ({ children }) => {
  const { user, toggleChatWindow, setUser } = useContext(AppContext);
  const { socket } = useContext(SocketContext);
  const [sessions, setSessions] = useState(undefined);
  const [currentSession, setCurrentSession] = useState(undefined);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatMetadata, setChatMetadata] = useState(undefined);
  const [chatIsSupport, setChatIsSupport] = useState(false);
  const [audio] = useState(new Audio(audioFile));
  const [newUnreadMessage, setNewUnreadMessage] = useState(false);
  const {
    querySessions,
    queryChats,
    getSession,
    createSession,
    hideSession,
    pinSession,
    unpinSession,
    createChat,
    CHAT_TYPE,
  } = useChat();
  const {
    queryUsers,
    queryCompanies,
    getUser,
    getCompany,
    queryUserChatSessions,
    queryStaffChatSessions,
    getStaff,
  } = useUsers();
  const currentChatSessionRef = useRef(null);
  const sessionsRef = useRef(null);
  const [isMinimizeHidden, setIsMinimizeHidden] = useState(false);

  useEffect(() => {
    audio.load();
  }, []);

  useEffect(() => {
    if (!user) {
      setSessions(undefined);
      setCurrentSession(undefined);
      currentChatSessionRef.current = null;
      sessionsRef.current = null;
      return;
    }

    // * Skip init if already have sessions
    if (sessions) return;

    // * Get sessions and check if any new messages exists
    init();
  }, [user]);

  const init = async () => {
    getUnreadMessages();
    getSessions();
  };

  const getUnreadMessages = async () => {
    const { data: _sessions } = await querySessions(
      {
        users: {
          $in: [user._id],
        },
        active: true,
        deleted: false,
      },
      null,
      -1,
      {},
      {
        updatedOn: -1,
        createdOn: -1,
      }
    );

    const { data: _latestNewMessage } = await queryChats(
      {
        session: { $in: _sessions.map((s) => s._id) },
        createdBy: { $ne: user._id },
        readOn: null,
      },
      1,
      1
    );

    setNewUnreadMessage(_latestNewMessage.length > 0);
  };

  const getSessions = async () => {
    // * Get sessions
    let _sessions = [];
    const _userIds = [];
    const _companyIds = [];
    const _sessionIds = [];

    if (user.support) {
      _sessions = (
        await queryStaffChatSessions({}, null, -1, null, {
          updatedOn: -1,
          createdOn: -1,
        })
      ).data.map((session) => {
        if (session.user?._id) _userIds.push(session.user._id);
        if (session.company?._id) _companyIds.push(session.company._id);

        // ! Temp
        session.closed = true;
        session.support = true;
        session.isStaff = true;
        _sessionIds.push(session._id);

        return session;
      });
    }

    if (!user?.support) {
      _sessions = (
        await queryUserChatSessions(
          user.company ?? user._id,
          {},
          null,
          -1,
          null,
          {
            updatedOn: -1,
            createdOn: -1,
          }
        )
      ).data.map((session) => {
        if (session.user?._id) _userIds.push(session.user._id);
        if (session.company?._id) _companyIds.push(session.company._id);
        _sessionIds.push(session._id);

        return session;
      });
    }

    const userData = await queryUsers(
      { _id: { $in: _userIds } },
      1,
      _userIds.length
    );

    const companyData = await queryCompanies(
      { _id: { $in: _companyIds } },
      1,
      _companyIds.length
    );

    const _pinnedSessions = [];
    const _normalSessions = [];

    for (const _session of _sessions) {
      const userMatch = userData.data.find((u) => u._id === _session.user._id);
      const companyMatch = companyData.data.find(
        (u) => u._id === _session.company._id
      );

      if (userMatch) {
        _session.endUser = { ...userMatch, isCompany: false };
      }

      if (companyMatch) {
        _session.endUser = { ...companyMatch, isCompany: true };
      }

      if (!_session.endUser) continue;

      if (user.chat?.pins?.find((cp) => cp === _session._id)) {
        _session.isPinned = true;
        _pinnedSessions.push(_session);
        continue;
      }

      _normalSessions.push(_session);
    }

    const _result = [..._pinnedSessions, ..._normalSessions];

    initUserConnectivity(_result);
    setSessions(_result);
    sessionsRef.current = _result;
  };

  const handleMessagePinned = (msg) => {
    if (!msg) return;

    const { chat: chatId, session } = JSON.parse(msg);

    const _sessions = sessionsRef.current.map((s) => {
      if (s._id !== session) return s;

      if (s.pins.find((p) => p === chatId)) {
        return s;
      }

      s.pins = [...s.pins, chatId];

      return s;
    });

    const _currentSession = {
      ...currentChatSessionRef.current,
      pins: [...currentChatSessionRef.current.pins, chatId],
    };

    setSessions(_sessions);
    setCurrentSession(_currentSession);
    sessionsRef.current = _sessions;
    currentChatSessionRef.current = _currentSession;
  };

  const handleMessageUnpinned = (msg) => {
    if (!msg) return;

    const { chat: chatId, session } = JSON.parse(msg);

    const _sessions = sessionsRef.current.map((s) => {
      if (s._id !== session) return s;

      s.pins = s.pins.filter((p) => p !== chatId);

      return s;
    });

    const _currentSession = {
      ...currentChatSessionRef.current,
      pins: currentChatSessionRef.current.pins.filter((p) => p !== chatId),
    };

    setSessions(_sessions);
    setCurrentSession(_currentSession);
    sessionsRef.current = _sessions;
    currentChatSessionRef.current = _currentSession;
  };

  useEffect(() => {
    if (!socket) return;

    socket.on("chats:incoming-msg", handleIncomingMessage);
    socket.on("chats:msg-pinned", handleMessagePinned);
    socket.on("chats:msg-unpinned", handleMessageUnpinned);
    // socket.on("user-online", handleUserConnectivity);

    socket.on("sessions:users-connectivity", handleUserConnectivity);

    return () => {
      socket.off();
    };
  }, [socket]);

  useEffect(() => {
    if (sessions?.find((session) => session.unread)) {
      setNewUnreadMessage(true);
      return;
    }

    setNewUnreadMessage(false);
  }, [sessions]);

  const _handleSetCurrentChatSession = async (sessionId, userId) => {
    if (!sessionId) {
      setCurrentSession(undefined);
      currentChatSessionRef.current = undefined;
      setChatMessages([]);
      setChatMetadata(undefined);
      return;
    }

    const currentUser = userId || user?._id;

    // * Get session data
    let _session = sessionsRef.current.find((s) => s._id === sessionId);

    if (!_session) {
      // * Find if not found (Only support )
      const { data } = await querySessions(
        { _id: sessionId, support: true },
        1,
        1
      );

      if (data.length === 0) {
        alert("Session not found");
        return;
      }

      _session = data[0];

      // * Get end-user info
      if (_session.staff === currentUser) {
        const endUserId = _session.users[0];
        _session.endUser = await getUser(endUserId);
        _session.isStaff = true;
      }

      // * End user is a staff
      if (
        _session.staff &&
        _session.staff !== currentUser &&
        currentUser !== undefined
      ) {
        _session.endUser = await getStaff(_session.staff);
        _session.isStaff = false;
      }
    }

    // * Set session to read
    setSessions((curr) => {
      return curr.map((_) => {
        if (_._id !== sessionId) {
          return _;
        }

        _.unread = false;
        return _;
      });
    });

    setChatMessages([]);
    setChatMetadata(undefined);
    setCurrentSession(_session);
    currentChatSessionRef.current = _session;
  };

  const handleIncomingMessage = async (data) => {
    const _message = JSON.parse(data);

    setNewUnreadMessage(true);

    if (currentChatSessionRef.current !== _message.session) {
      audio.play();
    }

    _handleSetLatestMessage(_message);
  };

  const _handleSetLatestMessage = async ({
    session,
    msg,
    attachments,
    images,
    createdOn,
    createdBy,
  }) => {
    // * Check session already exists in state list
    const _sessionIndex = sessionsRef.current.findIndex(
      (curr) => curr._id === session
    );

    if (sessionsRef.current[_sessionIndex]?.support) {
      return;
    }

    if (_sessionIndex >= 0) {
      const _sessions = _.cloneDeep(sessionsRef.current);
      _sessions[_sessionIndex].updatedOn = createdOn;
      _sessions[_sessionIndex].unread =
        currentChatSessionRef.current?._id === session ? false : true;
      _sessions[_sessionIndex].message = {
        msg,
        attachments: attachments?.length ?? 0,
        images: images?.length ?? 0,
        createdOn,
        createdBy,
        readOn:
          currentChatSessionRef.current?._id === session ? new Date() : null,
      };

      const _sorted = _reorderSessions(_sessions);

      setSessions(_sorted);
      sessionsRef.current = _sorted;
      return;
    }

    // * Get session data
    await _handleNewSession(user._id, session);
  };

  const handleStartChat = async (recipientId, sender, options = {}) => {
    let senderId = sender?.company ?? user?.company ?? sender?._id ?? user?._id;

    const { data: _sessions } = await querySessions(
      {
        users: {
          $in: [recipientId, senderId],
        },
        support: false,
        active: true,
        deleted: false,
      },
      null,
      -1,
      {
        users: 1,
      }
    );

    let _sessionId = undefined;

    // ! Temp: Get users where strictly equal
    const exactSession = _sessions.find(
      (session) =>
        _.isEqual(session.users, [senderId, recipientId]) ||
        _.isEqual(session.users, [recipientId, senderId])
    );

    if (exactSession) {
      _handleSetCurrentChatSession(exactSession._id, senderId);
      _sessionId = exactSession._id;
    }

    // * Possible new session
    if (!exactSession) {
      const newSession = await createSession([senderId, recipientId], false);
      await _handleNewSession(senderId, newSession._id);

      _handleSetCurrentChatSession(newSession._id, senderId);
      _sessionId = newSession._id;
    }

    // * Check if need to send work exchange highlight
    const { work } = options;

    if (work) {
      await createChat(_sessionId, CHAT_TYPE.IMPRESSION, {
        work,
      });
    }

    setChatIsSupport(false);
    toggleChatWindow();
  };

  const _handleNewSession = async (senderId, sessionId) => {
    const { data } = await queryUserChatSessions(senderId, {
      _id: sessionId,
    });

    if (data.length === 0) {
      return;
    }

    let [session] = data;

    if (session.company?._id) {
      const _company = await getCompany(session.company?._id);

      session.endUser = { ..._company, isCompany: true };
    }

    if (session.user?._id) {
      const _user = await getUser(session.user?._id);

      session.endUser = { ..._user, isCompany: false };
    }

    session.isPinned = false;

    const _sessions = _reorderSessions([...(sessions ?? []), session]);

    setSessions(_sessions);
    sessionsRef.current = _sessions;
  };

  const _handleToggleChatSupportWindow = () => {
    setChatIsSupport(true);
    toggleChatWindow();
  };

  const initUserConnectivity = (sessionsData) => {
    if (!sessionsData || (sessionsData && sessionsData.length === 0)) return;

    const endUserIds = sessionsData
      .filter((sd) => !!sd.endUser)
      .map((m) => m.endUser._id);

    const idsUniq = new Array(...new Set(endUserIds));

    socket?.emit(
      "message",
      JSON.stringify({
        event: "user-online:join",
        to: { users: idsUniq },
      })
    );
  };

  const handleUserConnectivity = (body) => {
    const { userId, online } = body;
    if (!sessions || sessions.length === 0) return;

    const temp = [];
    for (const _session of sessions) {
      if (_session.endUser._id !== userId) {
        temp.push(_session);
        continue;
      }
      _session.endUser.connectivity.online = online;

      setCurrentSession((tmp) => ({
        ...tmp,
        "endUser.connectivity.online": online,
      }));
      temp.push(_session);
    }

    setSessions(temp);
  };

  const _handleHideSession = async (sessionId) => {
    await hideSession(user._id, sessionId);

    const _sessions = sessionsRef.current.filter(
      (session) => session._id !== sessionId
    );

    setSessions(_sessions);
    sessionsRef.current = _sessions;
  };

  const _getIsHiddenMessage = (dateToCompare) => {
    if (!currentSession.hideSince) return false;

    return new Date(currentSession.hideSince) > new Date(dateToCompare);
  };

  const _handlePinSession = async (sessionId) => {
    await pinSession(sessionId);

    const _sessions = _reorderSessions(
      _.cloneDeep(sessionsRef.current).map((s) => {
        if (s._id === sessionId) {
          s.isPinned = true;
        }

        return s;
      })
    );

    setUser((curr) => {
      const _user = curr;
      _user.chat.pins.push(sessionId);
      return _user;
    });

    setSessions(_sessions);
    sessionsRef.current = _sessions;
  };

  const _handleUnpinSession = async (sessionId) => {
    await unpinSession(sessionId);

    const _sessions = _reorderSessions(
      _.cloneDeep(sessionsRef.current).map((s) => {
        if (s._id !== sessionId) return s;
        s.isPinned = false;
        return s;
      })
    );

    setUser((curr) => {
      curr.chat.pins = curr.chat.pins.filter((p) => p !== sessionId);
      return curr;
    });

    setSessions(_sessions);
    sessionsRef.current = _sessions;
  };

  /**
   * Sort sessions and order by `updatedOn` and prioritize pinned sessions
   * @param {} sessions
   * @returns
   */
  const _reorderSessions = (sessions) => {
    const _pinned = [];
    const _normal = [];

    for (const session of sessions) {
      if (session.isPinned) {
        _pinned.push(session);
        continue;
      }

      _normal.push(session);
    }

    function sortingFunction(curr, next) {
      const _currentDate = new Date(curr.updatedOn);
      const _nextDate = new Date(next.updatedOn);

      if (_currentDate > _nextDate) return -1;

      if (_currentDate === _nextDate) return 0;

      return 1;
    }

    return [..._pinned.sort(sortingFunction), ..._normal.sort(sortingFunction)];
  };

  const _handlePinMessage = (chatId) => {
    const _pins = currentChatSessionRef.current.pins;

    if (_pins.find((p) => p === chatId)) return;

    const _newSession = {
      ...currentChatSessionRef.current,
      pins: _pins,
    };

    const _sessions = sessionsRef.current.map((s) => {
      if (s._id !== currentChatSessionRef.current._id) return s;

      if (s.pins.find((p) => p === chatId)) {
        return s;
      }

      s.pins = [...s.pins, chatId];

      return s;
    });

    setSessions(_sessions);
    sessionsRef.current = _sessions;
    setCurrentSession(_newSession);
    currentChatSessionRef.current = _newSession;

    const event = new CustomEvent(`message:pinned-${chatId}`);
    document.dispatchEvent(event);
  };

  const _handleUnpinMessage = (chatId) => {
    const _newSession = currentChatSessionRef.current;

    _newSession.pins = _newSession.pins.filter((p) => p !== chatId);

    const _sessions = sessionsRef.current.map((s) => {
      if (s._id !== currentChatSessionRef.current._id) return s;

      s.pins = s.pins.filter((p) => p !== chatId);

      return s;
    });

    setSessions(_sessions);
    sessionsRef.current = _sessions;
    setCurrentSession(_newSession);
    currentChatSessionRef.current = _newSession;

    const event = new CustomEvent(`message:unpinned-${chatId}`);
    document.dispatchEvent(event);
  };

  const handleHideMinimize = (hidden) => {
    setIsH;
  };

  const chatSessionContextValues = useMemo(
    () => ({
      currentSession: currentSession,
      sessions: sessions,
      setSessions: setSessions,
      setCurrentSession: _handleSetCurrentChatSession,
      getCurrentSession: () => currentChatSessionRef.current,
      messages: chatMessages,
      setMessages: setChatMessages,
      chatMetadata: chatMetadata,
      setChatMetadata: setChatMetadata,
      isSupport: chatIsSupport,
      setIsSupport: setChatIsSupport,
      startChat: handleStartChat,
      toggleChatSupportWindow: _handleToggleChatSupportWindow,
      isNewMessage: newUnreadMessage,
      setLatestMessage: _handleSetLatestMessage,
      hideSession: _handleHideSession,
      isHiddenMessage: _getIsHiddenMessage,
      pinSession: _handlePinSession,
      unpinSession: _handleUnpinSession,
      pinMessage: _handlePinMessage,
      unpinMessage: _handleUnpinMessage,
      clearCurrentSession: () => {
        setCurrentSession(undefined);
        currentChatSessionRef.current = undefined;
        setChatMessages([]);
        setChatMetadata(undefined);
      },
      isMinimizeHidden,
      setIsMinimizeHidden,
    }),
    [
      currentSession,
      sessions,
      currentChatSessionRef,
      currentChatSessionRef.current,
      chatMessages,
      chatMetadata,
      chatIsSupport,
      newUnreadMessage,
      isMinimizeHidden,
    ]
  );

  return (
    <ChatSessionContext.Provider value={chatSessionContextValues}>
      {children}
    </ChatSessionContext.Provider>
  );
};

ChatSessionContext.propTypes = {};

export default ChatSessionContext;
